/**
 * WorkspaceCards — Icon card sub-navigation for the Management tab.
 *
 * Replaces the plain text sub-tab row with visually distinct workspace
 * cards (I-1 design).  The active card uses theme accent tokens so it
 * feels native to the current theme.
 *
 * Usage:
 *   <WorkspaceCards
 *     items={items}
 *     activeKey="performance"
 *     onSelect={(key) => setWorkSubTab(key)}
 *   />
 */

import React from 'react';
import styles from './WorkspaceCards.module.css';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

/**
 * Badge tone — maps 1:1 to Management tab color semantics (CLAUDE.md).
 *   critical = red    (blocking)
 *   warning  = yellow (warning)
 *   info     = blue   (informational / neutral)
 */
export type WorkspaceCardBadgeTone = 'critical' | 'warning' | 'info';

export interface WorkspaceCardBadge {
  /** Count to display inside the badge. Hide the badge by omitting it or passing count <= 0. */
  count: number;
  /** Severity tone — drives the badge color. */
  tone?: WorkspaceCardBadgeTone;
  /** Optional accessible label ("3 open flags, 1 blocking"). */
  ariaLabel?: string;
}

export interface WorkspaceCardItem {
  /** Unique key used for selection state (e.g. 'performance'). */
  key: string;
  /** Translated display label. */
  label: string;
  /** SVG icon rendered inside the card (should accept width/height). */
  icon: React.ReactNode;
  /** Optional: disabled state (e.g. Gantt when no project is selected). */
  disabled?: boolean;
  /** Optional: status badge overlaid on the icon. */
  badge?: WorkspaceCardBadge;
}

export interface WorkspaceCardsProps {
  items: WorkspaceCardItem[];
  activeKey: string;
  onSelect: (key: string) => void;
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const WorkspaceCards: React.FC<WorkspaceCardsProps> = ({
  items,
  activeKey,
  onSelect,
}) => {
  return (
    <div className={styles.row} role="tablist" aria-label="Workspace views">
      {items.map((item) => {
        const isActive = item.key === activeKey;
        return (
          <button
            key={item.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            disabled={item.disabled}
            className={`${styles.card} ${isActive ? styles.cardActive : ''}`}
            onClick={() => onSelect(item.key)}
          >
            <span className={styles.cardIcon}>
              {item.icon}
              {item.badge && item.badge.count > 0 && (
                <span
                  className={`${styles.cardBadge} ${
                    item.badge.tone === 'critical' ? styles.badgeCritical :
                    item.badge.tone === 'info'     ? styles.badgeInfo     :
                                                     styles.badgeWarning
                  }`}
                  aria-label={item.badge.ariaLabel ?? `${item.badge.count}`}
                >
                  {item.badge.count > 99 ? '99+' : item.badge.count}
                </span>
              )}
            </span>
            <span className={styles.cardLabel}>{item.label}</span>
          </button>
        );
      })}
    </div>
  );
};

export default WorkspaceCards;
